import { useCallback, useMemo, useState } from 'react';
import { buildImagePreviewActions } from './previewActionBuilders';
import type { PreviewAction } from './previewActionMenuModel';
import { resolveImagePreviewActionIds } from './previewActionRules';
import type { Asset, FilesApi, ResolvedFilesCapabilities } from './types';

const DOWNLOAD_ERROR = 'Could not download this file. Please try again.';

interface UseImagePreviewActionsOptions {
    asset: Asset | null;
    isImagePreview: boolean;
    patientId: string;
    api: FilesApi;
    capabilities: ResolvedFilesCapabilities;
    downloadFile: (asset: Asset) => Promise<void>;
    requestDelete: (asset: Asset) => void;
    requestEdit: (asset: Asset) => void;
    isDeleting: boolean;
    refreshAssets: () => Promise<void>;
}

export interface ImagePreviewActions {
    actions: PreviewAction[];
    error: string | null;
    clearError: () => void;
}

// Pending state is keyed by assetId so it doesn't follow the user to the next file.
function useTrackedDownload(downloadFile: (asset: Asset) => Promise<void>) {
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const clearError = useCallback(() => setError(null), []);

    const download = useCallback(
        async (target: Asset) => {
            setError(null);
            setDownloadingKey(String(target.assetId));
            try {
                await downloadFile(target);
            } catch {
                setError(DOWNLOAD_ERROR);
            } finally {
                setDownloadingKey(null);
            }
        },
        [downloadFile]
    );

    return { downloadingKey, download, error, clearError };
}

function useTrackedProfilePicture(
    api: FilesApi,
    patientId: string,
    refreshAssets: () => Promise<void>
) {
    const [settingProfileKey, setSettingProfileKey] = useState<string | null>(null);

    const setAsProfilePicture = useCallback(
        async (target: Asset) => {
            const setProfilePicture = api.setProfilePicture;
            if (!target.patientFileId || !setProfilePicture) return;
            setSettingProfileKey(String(target.assetId));
            try {
                await setProfilePicture(patientId, target.patientFileId);
            } catch {
                // Per the FilesApi contract the host has already shown this failure.
                return;
            } finally {
                setSettingProfileKey(null);
            }
            await refreshAssets();
        },
        [api, patientId, refreshAssets]
    );

    return { settingProfileKey, setAsProfilePicture };
}

export function useImagePreviewActions(
    options: UseImagePreviewActionsOptions
): ImagePreviewActions {
    const { asset, isImagePreview, api, capabilities, isDeleting, requestDelete, requestEdit } =
        options;
    const { downloadingKey, download, error, clearError } = useTrackedDownload(
        options.downloadFile
    );
    const { settingProfileKey, setAsProfilePicture } = useTrackedProfilePicture(
        api,
        options.patientId,
        options.refreshAssets
    );
    const { showPreviewActions, canDelete, canRename, canSetProfilePicture } = capabilities;
    const hasSetProfilePictureApi = Boolean(api.setProfilePicture);

    const actions = useMemo<PreviewAction[]>(() => {
        if (!asset || !isImagePreview) return [];
        const key = String(asset.assetId);
        const ids = resolveImagePreviewActionIds({
            asset,
            showPreviewActions,
            canDelete,
            canRename,
            canSetProfilePicture,
            hasSetProfilePictureApi
        });
        return buildImagePreviewActions(ids, {
            pending: {
                'set-profile-picture': settingProfileKey === key,
                download: downloadingKey === key,
                delete: isDeleting
            },
            onSelect: {
                'set-profile-picture': () => void setAsProfilePicture(asset),
                edit: () => requestEdit(asset),
                download: () => void download(asset),
                delete: () => requestDelete(asset)
            }
        });
    }, [
        asset,
        isImagePreview,
        showPreviewActions,
        canDelete,
        canRename,
        canSetProfilePicture,
        hasSetProfilePictureApi,
        settingProfileKey,
        downloadingKey,
        isDeleting,
        setAsProfilePicture,
        download,
        requestDelete,
        requestEdit
    ]);

    return { actions, error, clearError };
}
